import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider, removeOldestQuery } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import { startPostHogErrors } from './lib/posthog-client'

// FOR-194: the session-expired dialog signs back in via an OAuth popup named
// 'forge-login'. Google's callback redirects that popup to the SPA root; close
// it here instead of booting a second copy of the app inside it. The opener's
// 2s poll notices the fresh session on its own.
if (window.opener && window.name === 'forge-login') {
  window.close()
}

// Recover from a stale lazy-loaded chunk instead of rendering a blank screen.
//
// Routes are code-split, so a browser holding an index.html from before a
// deploy will request chunk filenames that no longer exist afterwards. Vite
// fires vite:preloadError before the import rejects; reloading fetches the
// current index.html and its filenames.
//
// The event is deliberately NOT preventDefault()ed: cancelling it makes the
// wrapped import resolve with undefined, so every lazy route died on
// `m.PageName` with a misleading TypeError instead of the real chunk error
// (that is where the "undefined (reading 'PageName')" Sentry noise came
// from). Letting the rejection through hands the real error to
// RouteErrorBoundary, which shows a reload prompt if the reload below loses
// the race or has already been spent.
//
// The guard is time-based, not once-per-session: Forge tabs stay open all
// day across several deploys, and a sticky flag spends the tab's only reload
// on the first one. Two failures inside the window mean reloading is not
// helping, so it stops and the boundary's message takes over.
const RELOAD_KEY = 'chunk-reload-attempted-at'
const RELOAD_WINDOW_MS = 15_000

window.addEventListener('vite:preloadError', () => {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
  if (Date.now() - last < RELOAD_WINDOW_MS) {
    return
  }

  sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  window.location.reload()
})

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
  // Errors only: performance tracing and session replay are off org-wide
  // (they pushed the Sentry quota over and nobody used them).
  tracesSampleRate: 0,
  // Send default PII (Personal Identifiable Information)
  sendDefaultPii: true,
})

// PostHog error tracking pilot alongside Sentry (lib/posthog-errors.ts):
// errors only, production hostname only.
startPostHogErrors()

// Cache timing constants - gcTime must be >= maxAge for persistence to work correctly
// See: https://tanstack.com/query/v4/docs/framework/react/plugins/persistQueryClient
const STALE_TIME = 5 * 60 * 1000      // 5 minutes - data considered "fresh"
// A day, not half an hour. On expiry the persister does not drop stale entries,
// it calls removeClient() and deletes the WHOLE blob, and the timestamp measures
// time since the app was last open — so a 30 minute ceiling meant any meeting or
// lunch break guaranteed a cold, spinner-y start. gcTime only has to be >= maxAge
// (not equal to it), which is what the previous comment here misread.
//
// Paired with clearing the cache on logout in useAuth: without that, extending
// this leaves one user's data on a shared machine for a day instead of 30 min.
const GC_TIME = 24 * 60 * 60 * 1000   // 24 hours - keep in memory after unmount
const PERSIST_MAX_AGE = GC_TIME       // must be <= gcTime

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch when window regains focus (annoying UX)
      refetchOnWindowFocus: false,
      // Only retry failed requests once
      retry: 1,
      // Data is "fresh" for 5 minutes - won't refetch during this time
      // This is the key setting that prevents constant refetching when navigating
      staleTime: STALE_TIME,
      // Keep data in cache for 30 minutes even after component unmounts
      // Must be >= maxAge for localStorage persistence to work correctly
      gcTime: GC_TIME,
    },
  },
})

// Persist cache to localStorage - survives page refreshes
// Data loads instantly from localStorage while revalidating in background
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'forge-query-cache',
  // Without a retry, one oversized write past the ~5MB localStorage quota
  // throws and the entire persisted cache is dropped — silently, and for
  // every page, not just the one that overflowed. Dropping the oldest query
  // and trying again degrades to a smaller cache instead of no cache.
  retry: removeOldestQuery,
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        // maxAge must be <= gcTime, otherwise cache is garbage collected before it expires
        maxAge: PERSIST_MAX_AGE,
        // Buster string - change this to invalidate all cached data on deploy
        // Useful when making breaking API changes
        // v3: Drop lead responses that older builds persisted in localStorage.
        buster: 'v3',
        // Don't persist mutations or error states
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Only persist successful, explicitly cache-safe queries. Lead
            // pages opt out because their data changes continuously and can
            // contain contact details that do not belong in localStorage.
            // Events never persist: descriptions carry customers' emails and
            // amounts (the hooks also set meta.persist false; this is the backstop).
            if (query.queryKey[0] === 'events') return false
            // Compensation is everyone's pay: never in localStorage either.
            if (query.queryKey[0] === 'admin' && query.queryKey[1] === 'compensation') return false
            return query.state.status === 'success' && query.meta?.persist !== false
          },
        },
      }}
    >
      <App />
    </PersistQueryClientProvider>
  </StrictMode>,
)
